import { queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchMyNetwork } from '@/api/network';
import type { OrderHubOrder } from '@/api/order-hub/types';
import type { InlineFieldOption } from '@/components/shared/InlineEditorField';
import { useNetworkCompaniesById } from '@/hooks/useNetworkCompaniesById';

export type CompanyOptionsOrderInput = Pick<
  OrderHubOrder,
  | 'buyerCompanyId'
  | 'supplierCompanyId'
  | 'receiverCompanyId'
  | 'vendorCompanyId'
>;

/** The (≤4) company ids an order can reference. */
function referencedIdsOf(
  order: CompanyOptionsOrderInput | undefined,
): string[] {
  return [
    order?.buyerCompanyId,
    order?.supplierCompanyId,
    order?.receiverCompanyId,
    order?.vendorCompanyId,
  ].filter((id): id is string => !!id);
}

/** Light, id-scoped company fetch: resolves only the handful of companies an
 *  order actually references (`companyIds=` filter) instead of pulling the
 *  whole network. Shared by the overview loader (prefetch → no name flash)
 *  and `useOrderHubCompanyOptions` below. The sorted-ids key keeps the cache
 *  stable regardless of field order; it never collides with the full-network
 *  `['network-companies','by-id']` slot (different length + segment). */
export function referencedCompaniesQueryOptions(ids: string[]) {
  const sorted = [...ids].sort();
  return queryOptions({
    queryKey: ['network-companies', 'by-ids', sorted] as const,
    queryFn: () =>
      fetchMyNetwork(
        `companyIds=${sorted.join(',')}&isDropDown=true&includeOwnCompany=true`,
      ),
    enabled: sorted.length > 0,
    staleTime: 10 * 60_000,
  });
}

/** Company-select options for the overview tab, plus a name lookup for the
 *  triggers. Split into two queries so opening an order is cheap:
 *  - **display (always):** the ≤4 referenced companies via `companyIds=` — a
 *    tiny fetch that powers the section-card names.
 *  - **picklist (lazy):** the full network list, fetched *only* once the user
 *    opens a company select (`picklistEnabled`). Until then, `options` still
 *    holds the referenced companies so the current value renders and stays
 *    selectable. Any referenced company outside the network falls back to its
 *    raw id as the label (unchanged behaviour). */
export type UseOrderHubCompanyOptionsReturn = ReturnType<
  typeof useOrderHubCompanyOptions
>;

export function useOrderHubCompanyOptions(
  order: CompanyOptionsOrderInput | undefined,
  opts?: { picklistEnabled?: boolean },
): {
  options: InlineFieldOption[];
  nameById: (id: string | null | undefined) => string | null;
  isLoading: boolean;
  isPicklistLoading: boolean;
} {
  const ids = useMemo(() => referencedIdsOf(order), [order]);

  const referenced = useQuery(referencedCompaniesQueryOptions(ids));
  const { byId: networkById, isLoading: networkLoading } =
    useNetworkCompaniesById({ enabled: opts?.picklistEnabled ?? false });

  const referencedById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of referenced.data?.data ?? []) map.set(c.id, c.name);
    return map;
  }, [referenced.data]);

  const options = useMemo<InlineFieldOption[]>(() => {
    const map = new Map<string, string>();
    // Full network once the picklist has loaded…
    for (const c of networkById.values()) map.set(c.id, c.name);
    // …plus the referenced companies as seeds so the current value renders
    // and can be re-selected even before (or without) the full list.
    for (const id of ids) {
      if (!map.has(id)) map.set(id, referencedById.get(id) ?? id);
    }
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [networkById, referencedById, ids]);

  function nameById(id: string | null | undefined): string | null {
    if (!id) return null;
    return referencedById.get(id) ?? networkById.get(id)?.name ?? id;
  }

  return {
    options,
    nameById,
    isLoading: referenced.isLoading,
    isPicklistLoading: (opts?.picklistEnabled ?? false) && networkLoading,
  };
}
